import { LANGUAGE } from "@/app/(protected)/asset-generation/components/ReviewAssets/components/constants";
import { setCurrentPageName, setCurrentStepName, setCurrentUserflow } from "@/app/context/AppProvider/actions";
import { Activity } from "@/components/ActivityCard";
import { variants } from "@/components/Button";
import { RaiFailedCompliancesProps } from "@/components/RaiErrorAccordion";
import { ENTITY_COLLECTION_NAMES, PERMISSIONS, SORT_ORDER } from "@/constants/constants";
import { BrandWithCdsMetadata } from "@/service/entity/brand/types";
import {
  DSTextGenerationApiResponseType,
  DSVideoScriptApiResponseType,
  PromtGenMetadata,
} from "@/service/entity/project-output/helpers/dsTypes";
import { ProjectModelType } from "@/service/entity/project/model";

export type AttributesType = { [key: string]: string };

export type RequestStatus = "INITIATED" | "APPROVED" | "PENDING";

export type ImageFormats = "jpeg" | "png" | "psd";

export type variantType = "error" | "success" | "checkSuccess" | "warning" | "error-high" | "caution";

export type statusType = "pending" | "failure" | "successful" | "failed";

export type raiAssetType = "Text" | "Image" | "Video" | "Text & Image";

export type PermissionType = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export type LanguageFields = {
  description: string;
};

export type SupportedLanguage = {
  [key in LANGUAGE]: LanguageFields;
};

export interface ButtonAttributesProps {
  dataAttributes?: AttributesType;
  rightButtonEventClass?: string;
  leftButtonEventClass?: string;
}

export interface BaseModalFooterProps extends ButtonAttributesProps {
  onClose?: (text?: string) => void;
  onConfirm?: () => void;
  onButtonClick?: (text?: string) => void;
  leftButtonText?: string;
  rightButtonText?: string;
  leftButtonVariant?: variants;
  rightButtonVariant?: variants;
  isRaiModal?: boolean;
  error?: boolean;
  errorId?: string;
}

export interface BaseModalProps extends BaseModalFooterProps {
  isOpen?: boolean;
  modalTitle?: string;
  modalDescription?: string | React.ReactNode;
  children?: React.ReactNode;
  descriptionClassName?: string;
  contentClassName?: string;
  leftButtonVariant?: variants;
  hideDialogueTitle?: boolean;
  removeOverflow?: boolean;
}

export type FooterLink = {
  title: string;
  url: string;
};

export interface ContentItem {
  id: number;
  type?: string;
  link?: string;
  text: string;
}

export type TermsAndConditionsCardProps = {
  title: string;
  introduction: string;
  introTitle: string;
  introductionTncLogin: ContentItem[];
  readThrough: string;
  privacyPolicyTitle: string;
  sections: {
    title?: string;
    content: ContentItem[];
  }[];
};

export type FooterSectionParagraphLinks = {
  text: string;
  href: string;
};

export type FooterSectionParagraph = {
  text: string;
  points?: string[];
  highlights?: string[];
  boldText?: boolean;
  noTopMargin?: boolean;
  links?: FooterSectionParagraphLinks[];
};

export type FooterSectionContent = {
  id: string;
  heading?: string;
  linkName?: string;
  paragraphs?: FooterSectionParagraph[];
};

export type FooterPageContent = {
  title: string;
  introTitle?: string;
  subHeading?: string;
  introText: string;
  sections: FooterSectionContent[];
};

export type ErrorType = {
  message?: string;
  error?: string;
  error_description?: string;
};

export type response<T> = {
  success?: boolean;
  error?: string;
  data?: T;
};

export interface BrandLocation {
  brand: string;
  location: string;
}

export type roles = BrandLocation & {
  roles?: string[];
  permissions?: string[];
  agency: string;
  requestStatus?: string;
  brandData?: BrandWithCdsMetadata;
};

export type userDetails = {
  uid: string;
  id?: string;
  email?: string;
  agency?: string;
  ad_group?: string;
  roles: Array<roles>;
};

export type Profile = {
  given_name?: string;
  family_name?: string;
};

export enum RAI_STATUS {
  PASS = "PASS",
  FAIL = "FAIL",
}

export enum RAI_TYPE {
  IMAGE = "Image",
  TEXT = "Text",
}

export const RAI_SUCCESS_MESSAGE = "Responsible AI check passed";

export type OptionType = {
  value: string;
  label: string;
  id?: string;
};

export type RadioOptionType = {
  id: string;
  label: string;
  ariaLabel?: string;
};

export type WhereCondition = {
  field: string;
  operator: FirebaseFirestore.WhereFilterOp;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
};

export type SortOrderType = (typeof SORT_ORDER)[keyof typeof SORT_ORDER];

export type ContentType = {
  value: "description" | "images" | "videoScript" | "blog";
  action: "download" | "copy";
  content?: string;
};

export type ValidTextTypes = "description" | "videoScript";

export interface Timestamp {
  _seconds: number;
  _nanoseconds: number;
}

export type SelectedBrand = {
  brand: string;
  agency: string;
  requestStatus?: boolean;
};

export interface ResponseData {
  error: string;
  code?: string;
  message?: string;
  status?: string;
}

export interface ErrorResponse {
  details: {
    response: {
      status: number;
      statusText: string;
      data: ResponseData;
    };
  };
  success?: boolean;
}

export type URL_PERMISSIONS_MAPPING_TYPE = {
  [key in string]?: string | string[];
};

export interface ListItem {
  id: number;
  text: string | Array<{ id: number; text: string; type?: string; link?: string }>;
  list?: ListItem[];
}

// Define a type for each policy section
export interface PolicySection {
  TITLE: string;
  PART_ONE?: string;
  PART_TWO?: string;
  PART_THREE?: string;
  PART_FOUR?: string;
  PART_FIVE?: string;
  LIST?: ListItem[];
  CONTENT?: Array<{ id: number; text: string; type?: string; link?: string }>;
  PARAGRAPH_ONE?: string;
  PARAGRAPH_TWO?: string;
  PARAGRAPH_THREE?: string;
  END?: string;
}

export interface TermsOfServicePolicy {
  SECTION_ORDER: string[];
  [key: string]: PolicySection | string[];
}

export type ResourceType = typeof ENTITY_COLLECTION_NAMES.PROJECT | typeof ENTITY_COLLECTION_NAMES.PROJECT_OUTPUT;

export type AssetInfo = {
  dimensionName?: string;
  fileType?: string;
  fileName?: string;
  assetType: string;
};

export type CommonEventParameters = {
  brandName?: string;
  pageName?: string;
  userFlow?: string;
  stepName?: string;
  linkText?: string;
  linkSection?: string;
  errorMessage?: string;
  errorType?: string;
  errorLocation?: string;
  imageId?: string;
  paginationNumber?: string;
  timeTaken?: string;
  overlayName?: string;
  fileStatus?: string;
  assetType?: string;
  raiStatus?: string;
  fileType?: string;
  descriptionType?: string;
  violations?: string | string[] | RaiFailedCompliancesProps[];
  thumbs?: string;
  insightName?: string;
  conceptType?: string;
  selectionName?: string;
  imageType?: string;
  selectionContent?: string;
  copyOutput?: string;
  addReferenceImage?: string;
  isTextOnImage?: string;
  aspectRatio?: string;
  translationName?: string;
  filename?: string;
  dimensionName?: string;
  no_of_assets_generated?: string;
  productType?: string;
  imageVariance?: string;
  noOfAssetsGenerated?: string;
  subscriptionType?: string;
  additionalFeedback?: string;
  feedbackDetails?: string;
  fileAttached?: string;
  copyText?: string;
  selectedScriptName?: string;
  isUsageRightsConfirmed?: boolean;
};

export interface ProjectMetaProps {
  projectId: string;
  projectType: string;
}

export interface EventDataConfig {
  stepName: string;
  pageName: string;
  userflow: string;
  stepNameSetter: typeof setCurrentStepName;
  pageNameSetter: typeof setCurrentPageName;
  userflowSetter: typeof setCurrentUserflow;
}

export type DataAttributesProps = Record<string, string>;

export enum Status {
  ACTIVE = "active",
  COMPLETED = "completed",
  DISABLED = "disabled",
  FAILED = "failed",
  RUNNING = "running",
  PENDING = "pending",
  SUCCESSFUL = "successful",
}

export type SupportedLanguages = {
  id: string;
  label: string;
  locale: string;
};

export type GetLanguageLocaleParams = {
  currentLanguage?: string;
  supportedLanguages?: SupportedLanguages[];
  primaryLanguageLocale?: string;
};

export interface ReferenceImage {
  url: string;
  fileId: string;
  fileName: string;
  base64?: string;
}

export interface BaseFileId {
  url?: string;
  base64?: string;
  fileId?: string;
  fileName?: string;
  raiOutput?: RAIResponse;
}

export interface FileWithId extends BaseFileId {
  file: File | null;
  id?: string;
  name?: string;
}

export interface HeaderDescriptionType {
  TITLE: string;
  DESCRIPTION: string;
  ADDITIONAL_DESCRIPTION?: string;
  HEADING_ID: string;
}

export interface NextActivityType {
  title: string;
  activities: Array<Activity>;
}

export interface RAIResponse {
  assetText?: string;
  assetType?: string;
  errorMessage?: string[];
  errorMessages?: string[];
  assetUrl?: string;
  successMessage?: string;
  complianceIds?: string[];
  status?: string;
  xRequestIdRaiCheck?: string;
}

export interface NextActivity {
  title: string;
  activities: Activity[];
}

export type ImageObject =
  | {
      pngURL: string;
      jpegURL: string;
      psdURL?: string;
      selectedSizeOption?: string | null;
      sizes?: Record<
        string,
        {
          pngURL: string;
          jpegURL?: string;
          psdURL?: string;
        }
      > | null;
    }
  | null
  | undefined;

export interface BuildLanguageMetaDataParams {
  project: ProjectModelType;
  inputImagePath?: string | string[];
  finalOutputImages: ImageObject[];
  updatedOriginalImages: ImageObject[];
  description?:
    | {
        title?: string;
        description?: string;
      }
    | DSTextGenerationApiResponseType
    | null;
  videoScript?: DSVideoScriptApiResponseType | null;
  images?: { providerIdentifier: string; provider: string } | null;
  promptGenMeta?: PromtGenMetadata;
}

export type FormFieldValue = string | number | boolean | object | null | undefined;
export type FormValues = Record<string, FormFieldValue>;
export type GAEventHandler = (eventName: string, data: FormValues) => void;

export interface ErrorCategory {
  category: string;
  status_code: number;
  message: string;
}

export type ApiError = {
  details?: {
    response?: {
      data?: {
        errors?: ErrorCategory[];
      };
    };
  };
};
